import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export const IVORY = "#f3ead2";
export const SLATE = "#a8aebd";
export const SLATE_DIM = "#6f7587";
export const GOLD = "#e9d6a2";
export const GOLD_DEEP = "#bfa874";
export const BLACK = "#000000";
export const HAIR = "1px solid rgba(233,214,162,0.10)";
export const Serif = { fontFamily: "Fraunces, serif" };

export const GOLD_FILL = {
  color: "#1a1408",
  background: "linear-gradient(135deg, #f7e9b8 0%, #d4b86a 25%, #f0dba0 50%, #b89752 75%, #e9d199 100%)",
  boxShadow: "inset 0 0 20px rgba(255,240,195,0.35), inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -1px 0 rgba(120,90,40,0.35)",
};

export const StarPulse = () => (
  <span
    aria-hidden
    className="absolute inset-0 pointer-events-none"
    style={{
      background: "radial-gradient(ellipse 55% 100% at 50% 50%, rgba(255,246,210,0.55) 0%, rgba(255,236,190,0.20) 40%, rgba(255,236,190,0) 78%)",
      animation: "msStarPulse 3.6s ease-in-out infinite",
      mixBlendMode: "screen",
    }}
  />
);

export const Em = ({ children }) => <span style={{ color: GOLD, fontStyle: "italic" }}>{children}</span>;

export const Kicker = ({ children, className = "" }) => (
  <p className={`text-[10px] tracking-[0.4em] uppercase ${className}`} style={{ color: GOLD_DEEP }}>{children}</p>
);

export const Ornament = () => (
  <div className="flex items-center justify-center gap-5 py-4" aria-hidden>
    <div style={{ height: 1, width: 96, background: `linear-gradient(90deg, transparent, ${GOLD_DEEP})` }} />
    <span style={{ ...Serif, color: GOLD, fontSize: 16 }}>✦</span>
    <div style={{ height: 1, width: 96, background: `linear-gradient(90deg, ${GOLD_DEEP}, transparent)` }} />
  </div>
);

export const Title = ({ children, as: Tag = "h2", size = "md", className = "", testId }) => {
  const sizes = { lg: "clamp(2.6rem, 5.6vw, 5rem)", md: "clamp(2rem, 3.8vw, 3.25rem)", sm: "clamp(1.6rem, 2.6vw, 2.2rem)" };
  return (
    <Tag className={`leading-[1.02] tracking-[-0.02em] ${className}`} style={{ ...Serif, color: IVORY, fontSize: sizes[size] }} data-testid={testId}>
      {children}
    </Tag>
  );
};

export const Body = ({ children, className = "", tone = SLATE }) => (
  <p className={`text-[15.5px] md:text-[16.5px] leading-[1.8] ${className}`} style={{ color: tone }}>{children}</p>
);

const Action = ({ to, href, onClick, type, testId, className, style, children }) => {
  if (to) return <Link to={to} data-testid={testId} className={className} style={style}>{children}</Link>;
  if (onClick || type) return <button type={type || "button"} onClick={onClick} data-testid={testId} className={className} style={style}>{children}</button>;
  const external = href?.startsWith("http");
  return (
    <a href={href} data-testid={testId} className={className} style={style} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
      {children}
    </a>
  );
};

const BTN = "relative inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-[10.5px] tracking-[0.32em] uppercase font-semibold overflow-hidden transition-transform duration-300 hover:-translate-y-px";

export const GoldBtn = ({ children, ...p }) => (
  <Action {...p} className={BTN} style={GOLD_FILL}>
    <StarPulse />
    <span className="relative z-[2]">{children}</span>
    <ArrowUpRight size={13} className="relative z-[2]" />
  </Action>
);

export const GhostBtn = ({ children, ...p }) => (
  <Action {...p} className={`${BTN} border transition-colors hover:bg-[rgba(233,214,162,0.06)]`} style={{ borderColor: "rgba(233,214,162,0.32)", color: GOLD }}>
    {children}
  </Action>
);

export const Section = ({ children, id, testId, className = "py-24 md:py-36", width = "max-w-6xl" }) => (
  <section id={id} className={`relative scroll-mt-16 ${className}`} style={{ background: BLACK }} data-testid={testId}>
    <div className={`${width} mx-auto px-6 md:px-12`}>{children}</div>
  </section>
);

export const Head = ({ kicker, children, body, center = true }) => (
  <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {kicker && <Kicker>{kicker}</Kicker>}
    <Title className="mt-6">{children}</Title>
    {body && <Body className="mt-8">{body}</Body>}
  </div>
);

export const Rows = ({ items, testId, labelWidth = "md:grid-cols-[260px_1fr]" }) => (
  <div className="mt-14" data-testid={testId}>
    {items.map(([label, copy, mark], i) => (
      <div key={label} className={`grid grid-cols-1 ${labelWidth} gap-3 md:gap-12 py-7`} style={{ borderTop: i === 0 ? HAIR : "none", borderBottom: HAIR }}>
        <div className="flex items-baseline gap-4">
          {mark && <span style={{ ...Serif, color: GOLD, fontSize: 22, minWidth: 34 }}>{mark}</span>}
          <span style={{ ...Serif, color: IVORY, fontSize: "clamp(1.1rem, 1.8vw, 1.35rem)", letterSpacing: "-0.008em" }}>{label}</span>
        </div>
        <p className="text-[15px] leading-[1.75]" style={{ color: SLATE }}>{copy}</p>
      </div>
    ))}
  </div>
);

export const Hero = ({ kicker, children, body, extra, testId }) => (
  <header className="relative overflow-hidden" data-testid={testId}>
    <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 60% at 50% 0%, rgba(233,214,162,0.08) 0%, transparent 60%)" }} />
    <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-24 md:pt-36 pb-12 text-center">
      <Kicker>{kicker}</Kicker>
      <Title as="h1" size="lg" className="mt-8 leading-[0.98]" testId={testId && `${testId}-headline`}>{children}</Title>
      {body && <Body className="mt-10 max-w-2xl mx-auto">{body}</Body>}
      {extra}
    </div>
  </header>
);

export const More = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="inline-flex items-center gap-1 underline underline-offset-4 decoration-[rgba(233,214,162,0.4)] transition-colors hover:text-white" style={{ color: GOLD }}>
    {children} <ArrowUpRight size={12} />
  </Link>
);

export const Bullets = ({ items, testId }) => (
  <ul className="mt-12 max-w-3xl mx-auto" data-testid={testId}>
    {items.map((b, i) => (
      <li key={i} className="grid grid-cols-[60px_1fr] gap-6 py-6" style={{ borderTop: i === 0 ? HAIR : "none", borderBottom: HAIR }}>
        <span className="text-[13px] tracking-[0.2em]" style={{ ...Serif, color: GOLD }}>{String(i + 1).padStart(2, "0")}</span>
        <span className="text-[16.5px] leading-[1.6]" style={{ color: IVORY }}>{b}</span>
      </li>
    ))}
  </ul>
);
